"use client";

import React, { useEffect } from "react";
import { useAtom } from "jotai";
import { useManagedWallet } from "@src/hooks/useManagedWallet";
import { useUser } from "@src/hooks/useUser";
import { useWhen } from "@src/hooks/useWhen";
import { settingsIdAtom } from "@src/store/settingsStore";
import { BootLoading } from "../BootLoadingProvider/BootLoadingProvider";
import { useServices } from "../ServicesProvider";
import { deriveWalletIsLoading } from "./deriveWalletIsLoading";
export const DEPENDENCIES = {
  useUser,
  useManagedWallet,
  useServices,
  BootLoading
};
export type ContextType = {
  address: string;
  /** True once the server-side wallet record exists. The address may still be empty while provisioning. */
  hasWallet: boolean;
  /** True once the wallet lookup has failed for good, which `hasWallet` alone cannot tell apart from a wallet still being provisioned. */
  isWalletLookupFailed: boolean;
  denom: string;
  isTrialing: boolean;
  creditAmount?: number;
  topUpMinAmountUsd: number;
};

/**
 * @private for testing only
 */
export const WalletProviderContext = React.createContext<ContextType>({} as ContextType);

/**
 * WalletProvider is a client only component. It blocks rendering of its children behind the shared
 * boot overlay until the initial wallet-existence lookup settles, so consumers never observe an
 * unsettled wallet. The gate deliberately ignores wallet creation (trial provisioning) — gating on
 * it would blank the app mid-onboarding.
 */
export const WalletProvider: React.FC<{
  children: React.ReactNode;
  dependencies?: typeof DEPENDENCIES;
}> = ({
  children,
  dependencies: d = DEPENDENCIES
}) => {
  const {
    analyticsService
  } = d.useServices();
  const [, setSettingsId] = useAtom(settingsIdAtom);
  const {
    user
  } = d.useUser();
  const {
    wallet: managedWallet,
    isInitializing: isManagedWalletInitializing,
    isLookupFailed: isManagedWalletLookupFailed
  } = d.useManagedWallet();
  const walletAddress = managedWallet?.address;
  const hasWallet = !!managedWallet;
  const isInitializing = deriveWalletIsLoading({
    hasAuthenticatedUserId: !!user?.userId,
    isManagedWalletLoading: isManagedWalletInitializing
  });
  useWhen(hasWallet, () => {
    analyticsService.identify({
      managedWallet: true
    });
    analyticsService.trackSwitch("connect_wallet", "managed", "Amplitude");
  });
  useEffect(() => {
    setSettingsId(walletAddress || null);
  }, [walletAddress, setSettingsId]);
  return <WalletProviderContext.Provider value={{
    address: walletAddress as string,
    hasWallet,
    isWalletLookupFailed: isManagedWalletLookupFailed,
    denom: managedWallet?.denom ?? "",
    isTrialing: !!managedWallet?.isTrialing,
    creditAmount: managedWallet?.creditAmount,
    topUpMinAmountUsd: managedWallet?.topUpMinAmountUsd ?? 20
  }} data-sentry-element="WalletProviderContext.Provider" data-sentry-component="WalletProvider" data-sentry-source-file="WalletProvider.tsx">
      {isInitializing ? <d.BootLoading /> : children}
    </WalletProviderContext.Provider>;
};

// Hook
export function useWallet() {
  return {
    ...React.useContext(WalletProviderContext)
  };
}